import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from "react";
import { CoreComposer } from "./CoreComposer";
import { CoreIcon } from "./CoreIcon";
import type {
  CoreComposerProps,
  DraftChange,
  DraftChangeStatus,
  DraftClause,
  DraftExportChanges,
  DraftExportFormat,
  DraftExportRequest,
  DraftVersion,
  MaybePromise,
} from "./types";

export type DraftFormatCommand =
  | "bold" | "italic" | "underline" | "strike"
  | "bulleted-list" | "numbered-list" | "link" | "undo" | "redo";

/**
 * Layout note. Both draft screens are fixed-frame: the document scrolls inside its
 * own pane and the instructions box stays pinned to the bottom of the conversation
 * column. That only works if the screen fills a frame of known height, which
 * `.di-redesign` now supplies (see the comment on it in document-intelligence.css —
 * it holds the whole story, including why a loaded draft used to have no chat box).
 * Nothing here needs to measure anything.
 */

/** Paragraphs of an imported draft rendered in the preview pane before it is truncated. */
export const DRAFT_PREVIEW_PARAGRAPH_LIMIT = 1200;

export interface BlankDraftScreenProps {
  composer: CoreComposerProps;
  examples?: string[];
  accepting?: string;
  busy?: boolean;
  selectedDraft?: { name: string; source?: string; previewText?: string } | null;
  onImportDraft?: (file: File) => MaybePromise;
  onChooseExistingDraft?: () => MaybePromise;
  onExampleSelect?: (instruction: string) => MaybePromise;
  onFormatCommand?: (command: DraftFormatCommand) => void;
  onParagraphStyleChange?: (style: string) => void;
}

export function BlankDraftScreen({
  composer,
  examples = [
    "Redline the definition of confidential information to require that material be marked or designated in writing.",
    "Review this draft against a playbook and apply our standard positions.",
    "Draft a mutual CDA using our approved template.",
  ],
  accepting = ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  busy = false,
  selectedDraft = null,
  onImportDraft,
  onChooseExistingDraft,
  onExampleSelect,
  onFormatCommand,
  onParagraphStyleChange,
}: BlankDraftScreenProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const upload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (file && onImportDraft) void onImportDraft(file);
  };

  const commands: Array<{ command: DraftFormatCommand; label: string; text: string }> = [
    { command: "bold", label: "Bold", text: "B" },
    { command: "italic", label: "Italic", text: "I" },
    { command: "underline", label: "Underline", text: "U" },
    { command: "strike", label: "Strikethrough", text: "S" },
    { command: "bulleted-list", label: "Bulleted list", text: "•" },
    { command: "numbered-list", label: "Numbered list", text: "1." },
    { command: "link", label: "Insert link", text: "↗" },
    { command: "undo", label: "Undo", text: "↶" },
    { command: "redo", label: "Redo", text: "↷" },
  ];

  const previewParagraphs = selectedDraft?.previewText
    ? selectedDraft.previewText.split(/\n{2,}/).filter(Boolean)
    : [];
  const previewTruncated = previewParagraphs.length > DRAFT_PREVIEW_PARAGRAPH_LIMIT;

  return (
    <main className="di-core-screen di-core-blank-draft">
      <section className="di-core-blank-draft__conversation" aria-label="Draft instructions">
        <div className="di-core-blank-draft__intro">
          <p className="di-core-kicker">New draft</p>
          <h1>Import a document, or start from nothing</h1>
          <p>Attach the document you want to work on and tell DI what to do with it. Changes come back as tracked edits, each with its rationale.</p>
          <p className="di-core-overline">For example</p>
          <div className="di-core-example-list">
            {examples.map((example) => (
              <button
                type="button"
                key={example}
                disabled={!onExampleSelect || busy}
                title={!onExampleSelect ? "Draft instructions are not available." : undefined}
                onClick={() => void onExampleSelect?.(example)}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
        <div className="di-core-blank-draft__composer">
          <p className="di-core-overline">{selectedDraft ? `Instructions for ${selectedDraft.name}` : "Instructions"}</p>
          <CoreComposer
            {...composer}
            compact
            placeholder={composer.placeholder || "Describe what you want done with this draft…"}
            submitLabel={composer.submitLabel || "Send"}
          />
        </div>
      </section>
      <section className="di-core-blank-draft__editor" aria-label="Blank document editor">
        <div className="di-core-editor-toolbar" role="toolbar" aria-label="Document formatting">
          <label>
            <span className="di-core-sr-only">Paragraph style</span>
            <select
              defaultValue="paragraph"
              disabled={!onParagraphStyleChange}
              title={!onParagraphStyleChange ? "Formatting becomes available after a draft is loaded." : undefined}
              onChange={(event) => onParagraphStyleChange?.(event.currentTarget.value)}
            >
              <option value="paragraph">Paragraph</option>
              <option value="heading-1">Heading 1</option>
              <option value="heading-2">Heading 2</option>
            </select>
          </label>
          {commands.map((item) => (
            <button
              key={item.command}
              type="button"
              aria-label={item.label}
              disabled={!onFormatCommand}
              title={!onFormatCommand ? "Formatting becomes available after a draft is loaded." : item.label}
              onClick={() => onFormatCommand?.(item.command)}
            >
              {item.text}
            </button>
          ))}
          <span className="di-core-spacer" />
          <button type="button" disabled title="There are no tracked changes in a blank draft.">Show edits</button>
        </div>
        <div className={`di-core-blank-canvas ${selectedDraft?.previewText ? "di-core-blank-canvas--preview" : ""}`}>
          <div>
            <p>{selectedDraft ? "Draft ready for instructions." : "Write something, import a draft, or choose one already in KontractAI."}</p>
            {selectedDraft ? (
              <div className="di-core-imported-draft" role="status">
                <CoreIcon name="file" />
                <span><strong>{selectedDraft.name}</strong><small>{selectedDraft.source || "Selected draft"}</small></span>
              </div>
            ) : null}
            {selectedDraft?.previewText ? (
              <article className="di-core-draft-preview" aria-label={`${selectedDraft.name} preview`}>
                {previewParagraphs.slice(0, DRAFT_PREVIEW_PARAGRAPH_LIMIT).map((paragraph, index) => (
                  <p key={`${index}:${paragraph.slice(0, 24)}`}>{paragraph}</p>
                ))}
                {previewTruncated ? (
                  <p className="di-core-draft-preview__truncated">
                    Preview stops at the first {DRAFT_PREVIEW_PARAGRAPH_LIMIT} paragraphs. The whole document is still used
                    when Document Intelligence works on it.
                  </p>
                ) : null}
              </article>
            ) : null}
            {/*
              The "Import draft" and "Choose from KontractAI" buttons were removed here
              on owner instruction: "'Import draft' and 'choose from KontractAI' on the
              right side of the screen should be removed, and the user can add files
              through the files button instead."

              Everything they did is in the Files menu on the instruction box below —
              browse this computer, add from the project, and the KontractAI repository.
              The repository option was wired into THIS screen's Files menu before these
              buttons were deleted; it had previously existed only on the assistant
              screen, so removing them first would have quietly dropped the capability.

              "Import draft" also took a single file and ignored the rest. The Files
              menu accepts several at once and adds to what is already attached.
            */}
          </div>
        </div>
      </section>
    </main>
  );
}

export interface VersionHistoryDialogProps {
  open: boolean;
  versions: DraftVersion[];
  workingVersion: number;
  busyVersion?: number | null;
  onClose: () => void;
  onRestore?: (version: DraftVersion) => MaybePromise;
}

export function VersionHistoryDialog({
  open,
  versions,
  workingVersion,
  busyVersion,
  onClose,
  onRestore,
}: VersionHistoryDialogProps) {
  const titleId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose, open]);
  if (!open) return null;
  return (
    <div className="di-core-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="di-core-dialog di-core-dialog--versions" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="di-core-dialog__header">
          <h2 id={titleId}>Version history</h2>
          <button type="button" className="di-core-btn di-core-btn--secondary di-core-btn--icon" aria-label="Close version history" onClick={onClose}>
            <CoreIcon name="close" />
          </button>
        </header>
        <div className="di-core-version-list">
          {[...versions].sort((a, b) => b.number - a.number).map((version) => (
            <article key={version.id} className={version.number === workingVersion ? "is-current" : ""}>
              <strong>v{version.number}</strong>
              <div><p>{version.summary}</p><small>{version.byline}</small></div>
              {version.number === workingVersion ? (
                <span className="di-core-tag di-core-tag--accent">Working version</span>
              ) : (
                <button
                  type="button"
                  className="di-core-btn di-core-btn--secondary"
                  disabled={!onRestore || busyVersion !== null && busyVersion !== undefined}
                  title={!onRestore ? "Version restore is not available." : undefined}
                  onClick={() => void onRestore?.(version)}
                >
                  {busyVersion === version.number ? "Restoring…" : "Restore"}
                </button>
              )}
            </article>
          ))}
        </div>
        <footer className="di-core-dialog__footer">
          <span>Restoring makes that version the working document. Nothing is deleted.</span>
          <span className="di-core-spacer" />
          <button type="button" className="di-core-btn di-core-btn--primary" onClick={onClose}>Done</button>
        </footer>
      </section>
    </div>
  );
}

export interface ExportWordDialogProps {
  open: boolean;
  formats: DraftExportFormat[];
  initialFormatId?: string;
  workingVersion: number;
  openChangeCount: number;
  filename: string;
  busy?: boolean;
  onClose: () => void;
  onExport?: (request: DraftExportRequest) => MaybePromise<{ filename?: string } | void>;
  onDownloadAgain?: () => MaybePromise;
}

export function ExportWordDialog({
  open,
  formats,
  initialFormatId,
  workingVersion,
  openChangeCount,
  filename,
  busy = false,
  onClose,
  onExport,
  onDownloadAgain,
}: ExportWordDialogProps) {
  const titleId = useId();
  const [phase, setPhase] = useState<"form" | "done">("form");
  const [formatId, setFormatId] = useState(initialFormatId || formats[0]?.id || "");
  const [changes, setChanges] = useState<DraftExportChanges>("tracked");
  const [exportedFilename, setExportedFilename] = useState(filename);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPhase("form");
      setFormatId(initialFormatId || formats[0]?.id || "");
      setChanges("tracked");
      setExportedFilename(filename);
      setSubmitting(false);
    }
  }, [filename, formats, initialFormatId, open]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose, open]);

  if (!open) return null;
  const formatName = formats.find((format) => format.id === formatId)?.name || "Plain · no template";
  const exportDocument = async () => {
    if (!onExport) return;
    setSubmitting(true);
    try {
      const result = await onExport({ formatId, changes, workingVersion });
      if (result && "filename" in result && result.filename) setExportedFilename(result.filename);
      setPhase("done");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="di-core-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="di-core-dialog di-core-dialog--export" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        {phase === "form" ? (
          <>
            <header className="di-core-dialog__title"><h2 id={titleId}>Export to Word</h2></header>
            <div className="di-core-dialog__body">
              <label className="di-core-field">Export format
                <select value={formatId} onChange={(event) => setFormatId(event.currentTarget.value)}>
                  {formats.map((format) => <option key={format.id} value={format.id}>{format.name}</option>)}
                </select>
              </label>
              <fieldset className="di-core-fieldset">
                <legend>Changes</legend>
                {([
                  ["tracked", "Keep as tracked changes"],
                  ["clean", "Accept everything and export clean"],
                  ["comparison", `Comparison of version 1 against version ${workingVersion}`],
                ] as const).map(([id, label]) => (
                  <label key={id} className="di-core-radio">
                    <input type="radio" checked={changes === id} onChange={() => setChanges(id)} />
                    <span className="di-core-radio__dot" />
                    {label}
                  </label>
                ))}
              </fieldset>
              <p className="di-core-muted">
                {openChangeCount === 0
                  ? "All changes have been resolved."
                  : `${openChangeCount} change${openChangeCount === 1 ? " is" : "s are"} still open. Exporting with tracked changes keeps ${openChangeCount === 1 ? "it" : "them"} for the counterparty to see.`}
              </p>
            </div>
            <footer className="di-core-dialog__footer di-core-dialog__footer--actions">
              <button type="button" className="di-core-btn di-core-btn--secondary" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="di-core-btn di-core-btn--primary"
                disabled={!onExport || !formatId || busy || submitting}
                title={!onExport ? "Word export is not configured." : undefined}
                onClick={() => void exportDocument()}
              >
                {busy || submitting ? "Exporting…" : "Export"}
              </button>
            </footer>
          </>
        ) : (
          <>
            <header className="di-core-dialog__title"><h2 id={titleId}>Exported</h2></header>
            <div className="di-core-dialog__body">
              <div className="di-core-export-result">
                <CoreIcon name="file" />
                <strong>{exportedFilename}</strong>
                <span>{formatName}</span>
              </div>
              <p>Tracked changes preserved. Continue in Word for macros, add-ins or complex formatting.</p>
            </div>
            <footer className="di-core-dialog__footer di-core-dialog__footer--actions">
              <button type="button" className="di-core-btn di-core-btn--secondary" onClick={onClose}>Close</button>
              <button
                type="button"
                className="di-core-btn di-core-btn--primary"
                disabled={!onDownloadAgain}
                title={!onDownloadAgain ? "The exported file is not available for another download." : undefined}
                onClick={() => void onDownloadAgain?.()}
              >
                Download again
              </button>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

export interface LoadedDraftScreenProps {
  title: string;
  filename: string;
  documentMeta: string;
  // How many documents were attached as reference material for this review. Drives
  // the kicker, which previously claimed a playbook was used on every single review.
  referenceCount?: number;
  versions: DraftVersion[];
  workingVersion: number;
  clauses: DraftClause[];
  changes: DraftChange[];
  playbookName: string;
  playbookMet: number;
  playbookTotal: number;
  exportFormats: DraftExportFormat[];
  selectedExportFormatId?: string;
  verifyGate?: boolean;
  conversation?: Array<{ id?: string; role: string; content: string; created_at?: string }>;
  reviseComposer?: Omit<CoreComposerProps, "onSubmit">;
  onRestoreVersion?: (version: DraftVersion) => MaybePromise;
  onChangeDecision?: (change: DraftChange, decision: "accept" | "reject") => MaybePromise;
  onExport?: (request: DraftExportRequest) => MaybePromise<{ filename?: string } | void>;
  onDownload?: () => MaybePromise;
  onDownloadExport?: () => MaybePromise;
  onExportFormatChange?: (formatId: string) => void;
  onRevise?: (prompt: string, mode: "edit" | "answer") => MaybePromise;
}

function clauseText(clause: DraftClause, replacement: boolean) {
  return `${clause.before}${replacement ? clause.replacement : clause.original}${clause.after}`;
}

export function LoadedDraftScreen({
  title,
  filename,
  documentMeta,
  referenceCount = 0,
  versions,
  workingVersion,
  clauses,
  changes,
  playbookName,
  playbookMet,
  playbookTotal,
  exportFormats,
  selectedExportFormatId,
  verifyGate = true,
  conversation = [],
  reviseComposer,
  onRestoreVersion,
  onChangeDecision,
  onExport,
  onDownload,
  onDownloadExport,
  onExportFormatChange,
  onRevise,
}: LoadedDraftScreenProps) {
  const maxVersion = Math.max(workingVersion, ...versions.map((version) => version.number));
  const [currentVersion, setCurrentVersion] = useState(workingVersion);
  const [showEdits, setShowEdits] = useState(true);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);
  const [decisionBusy, setDecisionBusy] = useState("");
  const [mode, setMode] = useState<"edit" | "answer">("edit");
  const [formatId, setFormatId] = useState(selectedExportFormatId || exportFormats[0]?.id || "");
  const [statuses, setStatuses] = useState<Record<string, DraftChangeStatus>>(
    Object.fromEntries(changes.map((change) => [change.id, change.status || "open"]))
  );

  useEffect(() => setCurrentVersion(workingVersion), [workingVersion]);
  useEffect(() => setFormatId(selectedExportFormatId || exportFormats[0]?.id || ""), [exportFormats, selectedExportFormatId]);
  useEffect(() => {
    setStatuses((current) => Object.fromEntries(changes.map((change) => [change.id, current[change.id] || change.status || "open"])));
  }, [changes]);

  const visibleChanges = useMemo(
    () => changes.filter((change) => change.introducedInVersion <= currentVersion),
    [changes, currentVersion]
  );
  const openCount = visibleChanges.filter((change) => statuses[change.id] === "open").length;
  const changeById = useMemo(() => new Map(changes.map((change) => [change.id, change])), [changes]);

  const restore = async (version: DraftVersion) => {
    if (!onRestoreVersion) return;
    setRestoringVersion(version.number);
    try {
      await onRestoreVersion(version);
      setCurrentVersion(version.number);
      setVersionDialogOpen(false);
    } finally {
      setRestoringVersion(null);
    }
  };

  const decide = async (change: DraftChange, decision: "accept" | "reject") => {
    if (!onChangeDecision) return;
    setDecisionBusy(change.id);
    try {
      await onChangeDecision(change, decision);
      setStatuses((current) => ({ ...current, [change.id]: decision === "accept" ? "accepted" : "rejected" }));
    } finally {
      setDecisionBusy("");
    }
  };

  return (
    <main className="di-core-screen di-core-loaded-draft">
      <div className="di-core-draft-toolbar" role="toolbar" aria-label="Draft tools">
        <div className="di-core-segmented" aria-label="Tracked change display">
          <button type="button" aria-pressed={showEdits} onClick={() => setShowEdits(true)}>Show edits</button>
          <button type="button" aria-pressed={!showEdits} onClick={() => setShowEdits(false)}>Final</button>
        </div>
        <button type="button" className="di-core-btn di-core-btn--secondary" onClick={() => setVersionDialogOpen(true)}>
          <CoreIcon name="clock" /> Version history · v{currentVersion} of {maxVersion}
        </button>
        <span className="di-core-spacer" />
        <label className="di-core-template-select">
          <span className="di-core-sr-only">Export template</span>
          <select value={formatId} onChange={(event) => {
            setFormatId(event.currentTarget.value);
            onExportFormatChange?.(event.currentTarget.value);
          }}>
            {exportFormats.map((format) => <option key={format.id} value={format.id}>Template: {format.name}</option>)}
          </select>
        </label>
        <button
          type="button"
          className="di-core-btn di-core-btn--secondary"
          disabled={!onDownload}
          title={!onDownload ? "Draft download is not available." : undefined}
          onClick={() => void onDownload?.()}
        >
          <CoreIcon name="download" /> Download
        </button>
        <button
          type="button"
          className="di-core-btn di-core-btn--primary"
          disabled={!onExport || !exportFormats.length}
          title={!onExport ? "Word export is not configured." : undefined}
          onClick={() => setExportDialogOpen(true)}
        >
          Export to Word
        </button>
      </div>

      {verifyGate && (
        <div className="di-core-verification" role="note">
          <CoreIcon name="warning" />
          <span>AI-generated draft. Verify accuracy, completeness, citations and legal judgement before it leaves the firm.</span>
        </div>
      )}

      <div className="di-core-loaded-draft__body">
        <section className="di-core-document-viewport" aria-label="Draft document">
          <article className="di-core-paper">
            {/* This read "Redline · reviewed against a playbook" unconditionally —
                on every review, whether a playbook was involved or not. Same class of
                fault as the invented clause numbers: a confident statement the screen
                had not checked. It now says what actually happened. */}
            <p className="di-core-kicker di-core-paper__kicker">
              {referenceCount > 0
                ? `Redline · reviewed against ${referenceCount} attached ${referenceCount === 1 ? "document" : "documents"}`
                : "Redline · reviewed against your instructions"}
            </p>
            <h1>{title}</h1>
            <p className="di-core-paper__meta">{documentMeta} · working version v{currentVersion}</p>
            <hr className="di-core-hr" />
            {clauses.map((clause, index) => {
              const change = clause.changeId ? changeById.get(clause.changeId) : undefined;
              const exists = Boolean(change && change.introducedInVersion <= currentVersion);
              const status = change ? statuses[change.id] : undefined;
              const previousHeading = index > 0 ? clauses[index - 1].heading : undefined;
              return (
                <div key={clause.id}>
                  {clause.heading && clause.heading !== previousHeading && <h2>{clause.heading}</h2>}
                  <p className="di-core-clause">
                    {/* Render the label only when the document actually has one.
                        An empty <strong> plus a space used to leave a stray indent;
                        an invented counter used to leave something worse. */}
                    {/* Do not print the label twice. In some documents a paragraph's
                        own text already begins with its clause label — Word stores it
                        as a literal run as well as applying it through numbering — and
                        rendering both makes it look like the numbering is still wrong,
                        which is exactly what the reader is checking for. */}
                    {clause.number && !String(clause.original || "").trimStart().startsWith(clause.number)
                      ? <><strong>{clause.number}</strong>{" "}</>
                      : null}
                    {!change || !exists || status === "rejected"
                      ? clauseText(clause, false)
                      : status === "accepted" || !showEdits
                        ? clauseText(clause, true)
                        : <>
                            {clause.before}
                            {clause.original && <del>{clause.original}</del>}
                            {clause.replacement && <ins>{clause.replacement}</ins>}
                            {clause.after}
                          </>}
                  </p>
                </div>
              );
            })}
          </article>
        </section>

        <aside className="di-core-changes" aria-label="Tracked changes">
          <header><p className="di-core-kicker">Tracked changes</p><span>{openCount} of {visibleChanges.length} open</span></header>
          <div className="di-core-changes__scroll">
            {visibleChanges.length > 0 ? (
              <>
                <article className="di-core-coverage">
                  <p className="di-core-overline">Playbook coverage</p>
                  <strong>{playbookName}</strong>
                  <div><span><i style={{ width: `${playbookTotal ? Math.round((playbookMet / playbookTotal) * 100) : 0}%` }} /></span><small>{playbookMet} of {playbookTotal} met</small></div>
                  <p>{visibleChanges.length} required an edit · each is a card below.</p>
                </article>
                {visibleChanges.map((change) => {
                  const status = statuses[change.id] || "open";
                  if (status !== "open") {
                    return (
                      <div className="di-core-change-resolved" key={change.id}>
                        <CoreIcon name={status === "accepted" ? "check" : "x"} size={14} />
                        {change.position} · {change.clause} {status}
                      </div>
                    );
                  }
                  return (
                    <article className="di-core-change-card" key={change.id}>
                      <div><span className="di-core-tag di-core-tag--accent">{change.position} · {change.clause}</span><small>DI · {change.timestamp || "now"}</small></div>
                      <p>{change.rationale}</p>
                      <p className="di-core-muted">{change.grounding}</p>
                      <div className="di-core-actions">
                        <button
                          type="button"
                          className="di-core-btn di-core-btn--primary"
                          disabled={!onChangeDecision || Boolean(decisionBusy)}
                          title={!onChangeDecision ? "Change decisions are not available." : undefined}
                          onClick={() => void decide(change, "accept")}
                        >
                          {decisionBusy === change.id ? "Saving…" : "Accept"}
                        </button>
                        <button
                          type="button"
                          className="di-core-btn di-core-btn--secondary"
                          disabled={!onChangeDecision || Boolean(decisionBusy)}
                          title={!onChangeDecision ? "Change decisions are not available." : undefined}
                          onClick={() => void decide(change, "reject")}
                        >
                          Reject
                        </button>
                      </div>
                    </article>
                  );
                })}
                <p className="di-core-muted">Redlines export to Word as tracked changes, with a comparison between version 1 and the latest version.</p>
              </>
            ) : (
              <p className="di-core-clean-version">
                Version {currentVersion} is the counterparty draft as it arrived · no edits have been applied. Restore a later version, or ask DI below to review it again.
              </p>
            )}
          </div>
          {/*
            The instructions box is not conditional. A loaded draft that renders
            without one is a dead surface — the document is on screen and there is
            no way to ask for anything — so an omitted `reviseComposer` degrades to
            a disabled box that says why, rather than to no box at all.
          */}
          <footer className="di-core-changes__footer" aria-label="Draft instructions">
            <div className="di-core-segmented">
              <button type="button" aria-pressed={mode === "edit"} onClick={() => setMode("edit")}>Edit the draft</button>
              <button type="button" aria-pressed={mode === "answer"} onClick={() => setMode("answer")}>Answer in thread</button>
            </div>
            <p>{mode === "edit" ? "DI will apply the requested changes to the draft as tracked edits." : "DI will respond in the conversation without modifying the draft."}</p>
            {conversation.length > 0 && (
              <div className="di-core-draft-conversation" aria-label="Draft conversation">
                {conversation.slice(-6).map((message, index) => (
                  <div key={message.id || `${message.role}-${index}`} className={`is-${message.role}`}>
                    <strong>{message.role === "user" ? "You" : "Document Intelligence"}</strong>
                    <p>{message.content}</p>
                  </div>
                ))}
              </div>
            )}
            <CoreComposer
              {...(reviseComposer || {})}
              compact
              placeholder={reviseComposer?.placeholder || "Ask about this draft, or describe another revision…"}
              submitLabel={reviseComposer?.submitLabel || "Send"}
              onSubmit={onRevise ? (prompt) => onRevise(prompt, mode) : undefined}
            />
            {!onRevise && (
              <p className="di-core-muted">Draft instructions are not available for this document.</p>
            )}
          </footer>
        </aside>
      </div>

      <VersionHistoryDialog
        open={versionDialogOpen}
        versions={versions}
        workingVersion={currentVersion}
        busyVersion={restoringVersion}
        onClose={() => setVersionDialogOpen(false)}
        onRestore={onRestoreVersion ? restore : undefined}
      />
      <ExportWordDialog
        open={exportDialogOpen}
        formats={exportFormats}
        initialFormatId={formatId}
        workingVersion={currentVersion}
        openChangeCount={openCount}
        filename={filename}
        onClose={() => setExportDialogOpen(false)}
        onExport={onExport}
        onDownloadAgain={onDownloadExport}
      />
    </main>
  );
}
